import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { Button } from "../components/ui/button";
import {
  Upload,
  FileText,
  Trash2,
  Download,
  Loader2,
  X,
  FolderOpen,
  Calendar,
  User,
  Lock,
  Unlock,
  ShieldAlert,
  KeyRound,
  Settings,
  ShieldCheck,
} from "lucide-react";

const TAG_COLORS = {
  contract: "bg-emerald-100 text-emerald-800 border-emerald-200",
  contract_annex: "bg-emerald-50 text-emerald-700 border-emerald-200",
  termination: "bg-red-100 text-red-800 border-red-200",
  id_card: "bg-blue-100 text-blue-800 border-blue-200",
  passport: "bg-blue-100 text-blue-800 border-blue-200",
  work_permit: "bg-blue-50 text-blue-700 border-blue-200",
  visa: "bg-blue-50 text-blue-700 border-blue-200",
  tax_number: "bg-blue-50 text-blue-700 border-blue-200",
  offer_letter: "bg-purple-100 text-purple-800 border-purple-200",
  nda: "bg-purple-100 text-purple-800 border-purple-200",
  onboarding_checklist: "bg-purple-50 text-purple-700 border-purple-200",
  job_description: "bg-purple-50 text-purple-700 border-purple-200",
  salary_slip: "bg-amber-100 text-amber-800 border-amber-200",
  bonus_letter: "bg-amber-100 text-amber-800 border-amber-200",
  payroll_form: "bg-amber-50 text-amber-700 border-amber-200",
  expense_report: "bg-amber-50 text-amber-700 border-amber-200",
  leave_application: "bg-cyan-100 text-cyan-800 border-cyan-200",
  sick_note: "bg-cyan-100 text-cyan-800 border-cyan-200",
  medical_certificate: "bg-cyan-100 text-cyan-800 border-cyan-200",
  performance_review: "bg-indigo-100 text-indigo-800 border-indigo-200",
  improvement_plan: "bg-orange-100 text-orange-800 border-orange-200",
  disciplinary_notice: "bg-red-100 text-red-800 border-red-200",
  warning_letter: "bg-red-100 text-red-800 border-red-200",
  training_certificate: "bg-teal-100 text-teal-800 border-teal-200",
  course_completion: "bg-teal-100 text-teal-800 border-teal-200",
  license: "bg-teal-50 text-teal-700 border-teal-200",
  background_check: "bg-slate-100 text-slate-800 border-slate-200",
  health_safety: "bg-slate-100 text-slate-800 border-slate-200",
  gdpr_consent: "bg-slate-100 text-slate-800 border-slate-200",
  medical_check: "bg-slate-100 text-slate-800 border-slate-200",
  other: "bg-slate-100 text-slate-700 border-slate-200",
};

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Vault setup / unlock / access-request panel — rendered at the top of the
// modal whenever the user can't perform protected actions yet.
// ---------------------------------------------------------------------------
function VaultPanel({
  isAdmin,
  vaultStatus,    // { configured, set_by, set_at, session_minutes }
  myAccess,       // { status, is_admin, decided_notes, ... }
  unlocked,
  unlockExpiresAt,
  onPinSetup,
  onUnlock,
  onLock,
  onRequestAccess,
  onOpenManageAccess,
}) {
  const [pin, setPin] = useState("");
  const [setupNew, setSetupNew] = useState("");
  const [setupCurrent, setSetupCurrent] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const accessStatus = myAccess?.status || "none";
  const canAttemptUnlock = isAdmin || accessStatus === "approved";

  // ----- States -----

  // 1) Vault not configured at all → admin sees setup form
  if (!vaultStatus?.configured) {
    if (!isAdmin) {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>HR Vault is not configured yet.</strong> Ask an admin to set the access code before any documents can be opened or downloaded.
          </div>
        </div>
      );
    }
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="vault-setup-panel">
        <div className="flex items-start gap-2 text-sm text-blue-900">
          <KeyRound className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <strong>Set the HR Vault access code.</strong> This code is required (in addition to login) to download, upload, edit or delete any HR document.
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-slate-600 mb-1 block">New access code (4-32 characters)</label>
            <input
              type="password"
              value={setupNew}
              onChange={(e) => setSetupNew(e.target.value)}
              minLength={4}
              maxLength={32}
              className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm font-mono"
              data-testid="vault-setup-new-pin"
            />
          </div>
          <Button
            onClick={async () => {
              setBusy(true);
              try { await onPinSetup({ new_pin: setupNew }); setSetupNew(""); }
              finally { setBusy(false); }
            }}
            disabled={busy || setupNew.length < 4}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="vault-setup-save-btn"
          >
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save code
          </Button>
        </div>
      </div>
    );
  }

  // 2) Already unlocked
  if (unlocked) {
    const minutes = unlockExpiresAt
      ? Math.max(0, Math.round((new Date(unlockExpiresAt) - Date.now()) / 60000))
      : 0;
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-3" data-testid="vault-unlocked-banner">
        <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
        <div className="flex-1 text-sm text-emerald-800">
          <strong>HR Vault unlocked</strong> — auto-locks in <strong>{minutes} min</strong>.
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={onOpenManageAccess} className="bg-white" data-testid="vault-manage-access-btn-unlocked">
            <Settings className="h-4 w-4 mr-1" />
            Manage access
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onLock} data-testid="vault-lock-btn">
          <Lock className="h-4 w-4 mr-1" />
          Lock
        </Button>
      </div>
    );
  }

  // 3) Staff who haven't been approved yet
  if (!canAttemptUnlock) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="vault-access-panel">
        <div className="flex items-start gap-2 text-sm text-amber-900">
          <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <strong>HR Vault access required.</strong> You can browse the document list, but downloading or editing requires admin approval and the access code.
          </div>
        </div>

        {accessStatus === "pending" && (
          <div className="text-sm text-slate-700 bg-white rounded p-3 border">
            ⏳ Your access request is <strong>pending</strong> admin approval.
            {myAccess?.requested_at && (
              <span className="text-xs text-slate-500 block mt-1">
                Submitted {new Date(myAccess.requested_at).toLocaleString()}
              </span>
            )}
          </div>
        )}

        {accessStatus === "rejected" && (
          <div className="text-sm text-red-700 bg-white rounded p-3 border border-red-200">
            ❌ Access denied{myAccess?.decided_notes ? `: ${myAccess.decided_notes}` : "."}
            <span className="text-xs text-slate-500 block mt-1">You can submit a new request below.</span>
          </div>
        )}

        {accessStatus !== "pending" && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600 block">
              Reason for access (optional, helps the admin decide)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
              placeholder="e.g. Need to upload my contract annex"
              data-testid="vault-access-reason"
            />
            <Button
              onClick={async () => {
                setBusy(true);
                try { await onRequestAccess(reason); setReason(""); }
                finally { setBusy(false); }
              }}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="vault-request-access-btn"
            >
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Apply for access
            </Button>
          </div>
        )}
      </div>
    );
  }

  // 4) Approved (or admin) — show PIN entry
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="vault-unlock-panel">
      <div className="flex items-start gap-2 text-sm text-blue-900">
        <KeyRound className="h-5 w-5 shrink-0 mt-0.5" />
        <div>
          <strong>HR Vault is locked.</strong> Enter the access code to download or edit documents. Listing is open to everyone with access.
        </div>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Access code</label>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && pin) { onUnlock(pin).then(() => setPin("")); } }}
            className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm font-mono"
            data-testid="vault-pin-input"
          />
        </div>
        <Button
          onClick={async () => {
            setBusy(true);
            try { const ok = await onUnlock(pin); if (ok) setPin(""); }
            finally { setBusy(false); }
          }}
          disabled={busy || !pin}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="vault-unlock-btn"
        >
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          <Unlock className="h-4 w-4 mr-1" />
          Unlock
        </Button>
      </div>
      {isAdmin && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-blue-200/60">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onOpenManageAccess}
            className="bg-white border-blue-200 text-blue-700 hover:bg-blue-50"
            data-testid="vault-manage-access-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage access requests
          </Button>
          <button
            type="button"
            onClick={() => {
              const nxt = window.prompt(
                "Set a new HR Vault access code (4-32 characters):\n\n" +
                "Tip: as admin you can rotate even without knowing the current code. " +
                "All staff sessions will be invalidated; staff stay approved."
              );
              if (!nxt || nxt.length < 4) {
                if (nxt !== null) toast.error("Code must be at least 4 characters");
                return;
              }
              onPinSetup({ new_pin: nxt });
            }}
            className="text-xs underline text-blue-700 hover:text-blue-900"
            data-testid="vault-forgot-code-btn"
          >
            Forgot / set a new code
          </button>
        </div>
      )}
      {isAdmin && vaultStatus?.set_by && (
        <p className="text-xs text-slate-500">
          Code last set by {vaultStatus.set_by} on {new Date(vaultStatus.set_at).toLocaleDateString()}.
        </p>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Admin "Manage Access" panel (rendered as a small inline overlay).
// ---------------------------------------------------------------------------
function ManageAccessPanel({ token, onClose }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/staff/hr-vault/access/requests`, {
        params: filter !== "all" ? { status: filter } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data.requests || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load access list");
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const decide = async (email, approve) => {
    const notes = approve ? null : window.prompt("Reason for rejection (optional, shown to staff):") || "";
    setBusyId(email);
    try {
      await axios.put(
        `${API_URL}/staff/hr-vault/access/requests/${encodeURIComponent(email)}/decide`,
        { approve, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approve ? "Access approved" : "Access rejected");
      await fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (email) => {
    if (!window.confirm(`Remove ${email} from the access list entirely?`)) return;
    setBusyId(email);
    try {
      await axios.delete(`${API_URL}/staff/hr-vault/access/requests/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Removed");
      await fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="vault-manage-access-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            HR Vault — Access management
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="px-5 py-3 border-b flex items-center gap-2 text-sm">
          <span className="text-slate-500">Filter:</span>
          {["all", "pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-2.5 py-1 rounded-md text-xs ${filter === s ? "bg-emerald-100 text-emerald-700 font-medium" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="overflow-y-auto p-3">
          {loading ? (
            <div className="text-center py-6"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-6">No access records.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2">User</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Reason / notes</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user_email} className="border-b last:border-b-0">
                    <td className="py-2">
                      <div className="font-medium">{r.user_full_name || r.user_email}</div>
                      <div className="text-xs text-slate-500">{r.user_email}</div>
                    </td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        r.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                        r.status === "rejected" ? "bg-red-100 text-red-700" :
                        "bg-amber-100 text-amber-700"
                      }`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-2 text-xs text-slate-600">
                      {r.reason && <div>📝 {r.reason}</div>}
                      {r.decided_notes && <div className="italic">↳ {r.decided_notes}</div>}
                    </td>
                    <td className="py-2 text-right space-x-1">
                      {r.status !== "approved" && (
                        <Button size="sm" variant="outline" disabled={busyId === r.user_email}
                          onClick={() => decide(r.user_email, true)}
                          className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`vault-approve-${r.user_email}`}>
                          Approve
                        </Button>
                      )}
                      {r.status === "approved" && (
                        <Button size="sm" variant="outline" disabled={busyId === r.user_email}
                          onClick={() => decide(r.user_email, false)}
                          className="border-red-200 text-red-700 hover:bg-red-50">
                          Revoke
                        </Button>
                      )}
                      {r.status === "pending" && (
                        <Button size="sm" variant="outline" disabled={busyId === r.user_email}
                          onClick={() => decide(r.user_email, false)}
                          className="border-red-200 text-red-700 hover:bg-red-50">
                          Reject
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" disabled={busyId === r.user_email}
                        onClick={() => revoke(r.user_email)}
                        className="text-slate-500 hover:text-red-600">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------
export default function StaffDocumentsModal({ staff, token, isAdmin, onClose }) {
  const [categories, setCategories] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedTag, setSelectedTag] = useState("contract");
  const [description, setDescription] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Vault state
  const [vaultStatus, setVaultStatus] = useState(null);
  const [myAccess, setMyAccess] = useState(null);
  const [vaultToken, setVaultToken] = useState(null);
  const [vaultExpiresAt, setVaultExpiresAt] = useState(null);
  const [showManageAccess, setShowManageAccess] = useState(false);

  const vaultHeaders = vaultToken ? { "X-HR-Vault-Token": vaultToken } : {};

  // ---- Fetchers ----
  const fetchVaultStatus = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        axios.get(`${API_URL}/staff/hr-vault/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/staff/hr-vault/access/me`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setVaultStatus(s.data);
      setMyAccess(m.data);
    } catch (e) {
      // soft-fail; vault panel will show appropriate message
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!staff?.id) return;
    setLoading(true);
    try {
      const [catRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/staff/document-categories`),
        axios.get(`${API_URL}/staff/${staff.id}/documents`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setCategories(catRes.data.categories || []);
      setDocs(docsRes.data.documents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [staff, token]);

  useEffect(() => {
    fetchData();
    fetchVaultStatus();
  }, [fetchData, fetchVaultStatus]);

  // Auto-relock when expiry passes
  useEffect(() => {
    if (!vaultExpiresAt) return;
    const ms = new Date(vaultExpiresAt) - Date.now();
    if (ms <= 0) { setVaultToken(null); setVaultExpiresAt(null); return; }
    const t = setTimeout(() => {
      setVaultToken(null);
      setVaultExpiresAt(null);
      toast.message("HR Vault auto-locked after 15 minutes");
    }, ms);
    return () => clearTimeout(t);
  }, [vaultExpiresAt]);

  // ---- Vault actions ----
  const handlePinSetup = async ({ new_pin, current_pin }) => {
    try {
      await axios.post(
        `${API_URL}/staff/hr-vault/setup`,
        { new_pin, current_pin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(current_pin ? "Access code rotated" : "Access code set");
      await fetchVaultStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save access code");
    }
  };

  const handleUnlock = async (pin) => {
    try {
      const r = await axios.post(
        `${API_URL}/staff/hr-vault/unlock`,
        { pin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVaultToken(r.data.token);
      setVaultExpiresAt(r.data.expires_at);
      toast.success(`Vault unlocked (auto-locks in ${r.data.session_minutes} min)`);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
        // refresh access state in case server says it changed
        if (detail.error === "access_not_granted") fetchVaultStatus();
      } else {
        toast.error(typeof detail === "string" ? detail : "Unlock failed");
      }
      return false;
    }
  };

  const handleLock = async () => {
    try {
      await axios.post(
        `${API_URL}/staff/hr-vault/lock`,
        {},
        { headers: { Authorization: `Bearer ${token}`, ...vaultHeaders } }
      );
    } catch (_) {}
    setVaultToken(null);
    setVaultExpiresAt(null);
  };

  const handleRequestAccess = async (reason) => {
    try {
      await axios.post(
        `${API_URL}/staff/hr-vault/access/request`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Access request submitted");
      await fetchVaultStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit request");
    }
  };

  // ---- Document actions ----
  const acceptFile = async (file) => {
    if (!file) return;
    if (!vaultToken) {
      toast.error("Unlock the HR Vault first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tag", selectedTag);
      if (description) fd.append("description", description);
      const r = await axios.post(`${API_URL}/staff/${staff.id}/documents`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
          ...vaultHeaders,
        },
      });
      toast.success(`Uploaded "${r.data.document.filename}"`);
      setDescription("");
      await fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "vault_expired") {
        setVaultToken(null);
        setVaultExpiresAt(null);
        toast.error("Vault session expired — please re-enter the access code");
      } else {
        toast.error(typeof detail === "object" ? detail?.message : detail || "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) {
      toast.error(
        "Couldn't read that drop — if you dragged from Outlook or another app, save the file to your desktop first.",
        { duration: 6000 }
      );
      return;
    }
    acceptFile(files[0]);
  };

  const handleDownload = async (doc) => {
    if (!vaultToken) {
      toast.error("Unlock the HR Vault first to download files");
      return;
    }
    try {
      const r = await axios.get(
        `${API_URL}/staff/${staff.id}/documents/${doc.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}`, ...vaultHeaders },
          responseType: "blob",
        }
      );
      const blob = new Blob([r.data], { type: doc.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Download failed");
    }
  };

  const handleDelete = async (doc) => {
    if (!vaultToken) {
      toast.error("Unlock the HR Vault first to delete files");
      return;
    }
    if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/staff/${staff.id}/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}`, ...vaultHeaders },
      });
      toast.success("Document deleted");
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Delete failed");
    }
  };

  // ── Validity / expiry tracking (2026-05-27, requested by Patricija) ──
  // Sets/clears an expiry date + reminder window per doc. The backend
  // runs a daily 07:00 CET digest that emails patricija@epholding.org
  // whenever any tracked doc is expiring within its reminder window or
  // already expired. UI: small buttons next to download/delete on each
  // doc row + a top-of-modal filter that lets HR see what's expiring.
  const [validityFilter, setValidityFilter] = useState("all"); // all|expiring|expired|archived

  const openValidityDialog = (doc) => {
    if (!vaultToken) {
      toast.error("Unlock the HR Vault first");
      return;
    }
    const existing = doc.expiry_date || "";
    const days = doc.reminder_days_before || 30;
    const newDate = window.prompt(
      `Set expiry date for:\n  ${doc.filename}\n\n` +
      `Format: YYYY-MM-DD or DD.MM.YYYY\n` +
      `Leave blank to STOP tracking this document.\n\n` +
      `Reminder window (days before expiry) will stay at ${days}.`,
      existing
    );
    if (newDate === null) return; // cancelled
    setDocValidity(doc, newDate.trim() || null, days);
  };

  const setDocValidity = async (doc, expiryDate, reminderDays) => {
    try {
      const r = await axios.put(
        `${API_URL}/staff/${staff.id}/documents/${doc.id}/validity`,
        { expiry_date: expiryDate, reminder_days_before: reminderDays },
        { headers: { Authorization: `Bearer ${token}`, ...vaultHeaders } }
      );
      toast.success(
        expiryDate
          ? `Tracking set — expires ${r.data.expiry_date}, reminders ${reminderDays}d before.`
          : "Validity tracking cleared."
      );
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed to set validity");
    }
  };

  const handleRenew = async (doc) => {
    if (!vaultToken) {
      toast.error("Unlock the HR Vault first");
      return;
    }
    const newDate = window.prompt(
      `Renew "${doc.filename}":\n\n` +
      `Enter the NEW expiry date (YYYY-MM-DD or DD.MM.YYYY).\n` +
      `Leave blank to mark this doc as renewed (a new doc will replace it).`,
      ""
    );
    if (newDate === null) return;
    const notes = window.prompt("Notes (optional):", "") || "";
    try {
      const payload = { notes };
      if (newDate.trim()) payload.new_expiry_date = newDate.trim();
      const r = await axios.post(
        `${API_URL}/staff/${staff.id}/documents/${doc.id}/renew`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, ...vaultHeaders } }
      );
      toast.success(
        r.data.new_status === "active"
          ? `Renewed — new expiry ${r.data.new_expiry_date}.`
          : "Doc marked as renewed."
      );
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Renew failed");
    }
  };

  const handleArchive = async (doc) => {
    if (!vaultToken) {
      toast.error("Unlock the HR Vault first");
      return;
    }
    const reason = window.prompt(
      `Archive "${doc.filename}"?\n\nReason (optional):`,
      ""
    );
    if (reason === null) return;
    try {
      await axios.post(
        `${API_URL}/staff/${staff.id}/documents/${doc.id}/archive`,
        { reason },
        { headers: { Authorization: `Bearer ${token}`, ...vaultHeaders } }
      );
      toast.success("Document archived — digest will ignore it.");
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Archive failed");
    }
  };

  // Compute the validity bucket for a doc — used by the row badge AND
  // the top-level filter. Mirrors the backend's `_classify_expiry`.
  const validityBucket = (d) => {
    const status = d.validity_status;
    if (status === "archived") return "archived";
    if (status === "renewed") return "renewed";
    if (!d.expiry_date) return "untracked";
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const exp = new Date(d.expiry_date); exp.setHours(0, 0, 0, 0);
    const delta = Math.round((exp - today) / 86400000);
    const window_ = d.reminder_days_before || 30;
    if (delta < 0) return "expired";
    if (delta <= 7) return "urgent";
    if (delta <= window_) return "soon";
    return "ok";
  };

  const validityBadge = (d) => {
    const b = validityBucket(d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (b === "expired") {
      const days = Math.abs(Math.round((new Date(d.expiry_date) - today) / 86400000));
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800 border border-red-200" data-testid={`validity-badge-${d.id}`}>🔴 Expired {days}d ago</span>;
    }
    if (b === "urgent") {
      const days = Math.round((new Date(d.expiry_date) - today) / 86400000);
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800 border border-orange-200" data-testid={`validity-badge-${d.id}`}>🟠 Expires in {days}d</span>;
    }
    if (b === "soon") {
      const days = Math.round((new Date(d.expiry_date) - today) / 86400000);
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200" data-testid={`validity-badge-${d.id}`}>🟡 Reminder · {days}d</span>;
    }
    if (b === "ok") {
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800 border border-green-200" data-testid={`validity-badge-${d.id}`}>🟢 Valid · {d.expiry_date}</span>;
    }
    if (b === "archived") {
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200" data-testid={`validity-badge-${d.id}`}>📦 Archived</span>;
    }
    if (b === "renewed") {
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`validity-badge-${d.id}`}>♻ Renewed</span>;
    }
    return null;
  };

  // Group docs by tag for nicer rendering — respecting the validity filter.
  const matchesValidity = (d) => {
    if (validityFilter === "all") return true;
    const b = validityBucket(d);
    if (validityFilter === "expiring") return b === "soon" || b === "urgent";
    if (validityFilter === "expired") return b === "expired";
    if (validityFilter === "archived") return b === "archived" || b === "renewed";
    return true;
  };
  const grouped = docs.filter(matchesValidity).reduce((acc, d) => {
    (acc[d.tag] = acc[d.tag] || []).push(d);
    return acc;
  }, {});

  // Counts per validity bucket — for the filter pills.
  const validityCounts = docs.reduce((acc, d) => {
    const b = validityBucket(d);
    if (b === "expired") acc.expired += 1;
    if (b === "urgent" || b === "soon") acc.expiring += 1;
    if (b === "archived" || b === "renewed") acc.archived += 1;
    acc.all += 1;
    return acc;
  }, { all: 0, expiring: 0, expired: 0, archived: 0 });

  const protectedDisabled = !vaultToken;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="staff-documents-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 text-white flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              HR Document Repository
            </h2>
            <p className="text-emerald-100 text-sm mt-1">
              {staff?.first_name} {staff?.last_name}
              {staff?.role ? ` · ${staff.role}` : ""}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Vault gate */}
          <VaultPanel
            isAdmin={isAdmin}
            vaultStatus={vaultStatus}
            myAccess={myAccess}
            unlocked={!!vaultToken}
            unlockExpiresAt={vaultExpiresAt}
            onPinSetup={handlePinSetup}
            onUnlock={handleUnlock}
            onLock={handleLock}
            onRequestAccess={handleRequestAccess}
            onOpenManageAccess={() => setShowManageAccess(true)}
          />

          {/* Tag picker + description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Document type
              </label>
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                disabled={protectedDisabled}
                className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="staff-doc-tag-select"
              >
                {categories.map((cat) => (
                  <optgroup key={cat.label} label={cat.label}>
                    {cat.tags.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Description (optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={protectedDisabled}
                className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="e.g. signed 09.05.2026, Annex 2"
              />
            </div>
          </div>

          {/* Drop zone */}
          <div
            onClick={() => !protectedDisabled && fileInputRef.current?.click()}
            onDrop={(e) => { if (!protectedDisabled) handleDrop(e); else { e.preventDefault(); } }}
            onDragOver={(e) => { e.preventDefault(); if (!protectedDisabled) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              protectedDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            } ${
              dragOver
                ? "border-emerald-500 bg-emerald-50"
                : "border-slate-300 hover:border-slate-400 bg-slate-50"
            }`}
            data-testid="staff-doc-dropzone"
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                <p className="text-sm text-slate-600">Uploading…</p>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                <p className="font-medium text-slate-700">
                  Drag &amp; drop documents here
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {protectedDisabled ? "Unlock the vault to upload" : "or click anywhere in this box to browse"}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    acceptFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                  data-testid="staff-doc-file-input"
                />
              </>
            )}
          </div>

          {/* Documents grouped by type */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                Documents ({docs.length})
              </h3>
              {/* Validity filter pills — added 2026-05-27 for HR Vault
                  expiry tracking. Counts come from validityBucket(). */}
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px] bg-white" data-testid="validity-filter">
                {[
                  { id: "all",      label: `All`,      count: validityCounts.all },
                  { id: "expiring", label: `Expiring`, count: validityCounts.expiring },
                  { id: "expired",  label: `Expired`,  count: validityCounts.expired },
                  { id: "archived", label: `Archived/Renewed`, count: validityCounts.archived },
                ].map((opt, i) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setValidityFilter(opt.id)}
                    className={`px-2.5 py-1 ${i > 0 ? "border-l border-slate-200" : ""} ${
                      validityFilter === opt.id
                        ? "bg-indigo-600 text-white"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid={`validity-filter-${opt.id}`}
                  >
                    {opt.label} <span className="opacity-70">({opt.count})</span>
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" />
              </div>
            ) : docs.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm border border-dashed rounded-lg">
                No documents on file yet.
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(grouped).map(([tag, list]) => {
                  const colorClass = TAG_COLORS[tag] || TAG_COLORS.other;
                  const tagLabel = list[0]?.tag_label || tag;
                  return (
                    <div key={tag} className="border rounded-lg overflow-hidden">
                      <div className={`px-3 py-2 text-xs font-medium flex items-center gap-2 border-b ${colorClass}`}>
                        <FileText className="h-3 w-3" />
                        {tagLabel} ({list.length})
                      </div>
                      <div className="divide-y">
                        {list.map((d) => (
                          <div
                            key={d.id}
                            className="px-3 py-2 flex items-center gap-3 hover:bg-slate-50"
                            data-testid={`staff-doc-row-${d.id}`}
                          >
                            <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 truncate">
                                {d.filename}
                              </div>
                              <div className="text-xs text-slate-500 flex flex-wrap items-center gap-3 mt-0.5">
                                <span>{formatBytes(d.file_size)}</span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {new Date(d.uploaded_at).toLocaleDateString()}
                                </span>
                                {d.uploaded_by && (
                                  <span className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    {d.uploaded_by.split("@")[0]}
                                  </span>
                                )}
                                {d.description && (
                                  <span className="italic">— {d.description}</span>
                                )}
                                {validityBadge(d)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => openValidityDialog(d)}
                              disabled={protectedDisabled}
                              title={protectedDisabled ? "Unlock vault to set expiry" : (d.expiry_date ? `Change expiry (currently ${d.expiry_date})` : "Set expiry / reminder")}
                              className="px-2 h-8 inline-flex items-center justify-center rounded-md text-xs border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                              data-testid={`validity-set-${d.id}`}
                            >
                              📅
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRenew(d)}
                              disabled={protectedDisabled}
                              title={protectedDisabled ? "Unlock vault to renew" : "Renew — set new expiry or mark renewed"}
                              className="px-2 h-8 inline-flex items-center justify-center rounded-md text-xs border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                              data-testid={`validity-renew-${d.id}`}
                            >
                              🔄
                            </button>
                            <button
                              type="button"
                              onClick={() => handleArchive(d)}
                              disabled={protectedDisabled}
                              title={protectedDisabled ? "Unlock vault to archive" : "Archive — stop reminders"}
                              className="px-2 h-8 inline-flex items-center justify-center rounded-md text-xs border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                              data-testid={`validity-archive-${d.id}`}
                            >
                              📦
                            </button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(d)}
                              disabled={protectedDisabled}
                              title={protectedDisabled ? "Unlock vault to download" : "Download"}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(d)}
                              disabled={protectedDisabled}
                              title={protectedDisabled ? "Unlock vault to delete" : "Delete"}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {showManageAccess && (
        <ManageAccessPanel token={token} onClose={() => setShowManageAccess(false)} />
      )}
    </div>
  );
}
